<template>
  <div
    class="credential-secret"
    :data-test="`credential-secret-${stage}`"
  >
    <!-- Estado: secret ainda nao visualizado -->
    <div
      v-if="!revealed"
      class="credential-secret__action"
    >
      <AmButton
        :label="$t('credentialSecret.reveal')"
        variant="maxPrimary"
        :icon="{ name: 'visibility', size: 'small', position: 'withLeftIcon' }"
        :disabled="loading"
        :data-test="`credential-secret-reveal-${stage}`"
        @click="reveal"
      />
      <AmTypography
        :text="$t('credentialSecret.revealHint')"
        typographyStyle="l7"
        textColor="text60"
        class="credential-secret__hint"
      />
    </div>

    <!-- Estado: secret ja visualizado (visualizacao unica) -->
    <div
      v-else
      class="credential-secret__action"
    >
      <div class="credential-secret__note">
        <AmIconMaterial
          name="lock"
          class="credential-secret__note-icon"
        />
        <AmTypography
          :text="$t('credentialSecret.alreadyRevealed')"
          typographyStyle="l7"
          textColor="text60"
        />
      </div>
      <AmButton
        :label="$t('credentialSecret.regenerate')"
        variant="maxGhost"
        :icon="{ name: 'autorenew', size: 'small', position: 'withLeftIcon' }"
        :disabled="loading"
        :data-test="`credential-secret-regenerate-${stage}`"
        @click="openRegenConfirm"
      />
    </div>

    <!-- Erro inline (falha ao revelar) -->
    <div
      v-if="errorMessage && !showSecretModal && !showRegenConfirm"
      class="credential-secret__error"
      :data-test="`credential-secret-error-${stage}`"
    >
      {{ errorMessage }}
    </div>

    <!-- Modal: secret revelado / regerado (exibido uma unica vez) -->
    <AmModal
      v-model="showSecretModal"
      maxWidth="500px"
      :title="resultMode === 'regenerate'
        ? $t('credentialSecret.modal.regenerateTitle')
        : $t('credentialSecret.modal.revealTitle')"
      :persistent="true"
      @close="closeSecretModal"
    >
      <div class="credential-secret__modal">
        <div class="credential-secret__warning">
          <AmIconMaterial
            name="warning"
            class="credential-secret__warning-icon"
          />
          <AmTypography
            :text="$t('credentialSecret.modal.oneTimeWarning')"
            typographyStyle="l7"
          />
        </div>

        <AmTypography
          :text="$t('credentialSecret.modal.secretLabel')"
          typographyStyle="l6"
          weight="medium"
          textColor="text80"
          class="credential-secret__modal-label"
        />
        <AmInput
          :modelValue="secret"
          inputCopy
          readOnly
          size="fullWidth"
          :data-test="`credential-secret-value-${stage}`"
        />

        <div class="credential-secret__modal-actions">
          <AmButton
            :label="$t('credentialSecret.modal.close')"
            variant="maxPrimary"
            @click="closeSecretModal"
          />
        </div>
      </div>
    </AmModal>

    <!-- Modal: confirmacao de regeracao (invalida o par atual) -->
    <AmModal
      v-model="showRegenConfirm"
      maxWidth="500px"
      :title="$t('credentialSecret.confirm.title')"
      :persistent="true"
      @close="closeRegenConfirm"
    >
      <div class="credential-secret__modal">
        <div class="credential-secret__warning">
          <AmIconMaterial
            name="warning"
            class="credential-secret__warning-icon"
          />
          <AmTypography
            :text="$t('credentialSecret.confirm.text')"
            typographyStyle="l7"
          />
        </div>

        <div
          v-if="errorMessage"
          class="credential-secret__error"
          :data-test="`credential-secret-regen-error-${stage}`"
        >
          {{ errorMessage }}
        </div>

        <div class="credential-secret__modal-actions credential-secret__modal-actions--split">
          <AmButton
            :label="$t('credentialSecret.confirm.cancel')"
            variant="maxGhost"
            :disabled="loading"
            @click="closeRegenConfirm"
          />
          <AmButton
            :label="$t('credentialSecret.confirm.confirm')"
            variant="maxPrimary"
            :disabled="loading"
            :data-test="`credential-secret-regenerate-confirm-${stage}`"
            @click="confirmRegenerate"
          />
        </div>
      </div>
    </AmModal>
  </div>
</template>

<script setup>
import { ref } from 'vue'
import { useI18n } from 'vue-i18n'
import {
  AmButton,
  AmIconMaterial,
  AmInput,
  AmModal,
  AmTypography
} from '@appmax_npm/ds-appmax-v3'
import { useHttpStore } from '@/stores/http'

const props = defineProps({
  // Id do app usado nos endpoints /app/credentials/{id}/...
  appId: {
    type: [String, Number],
    required: true
  },
  // Stage da API (nao confundir com o rotulo da UI): 'homolog' ou 'production'
  stage: {
    type: String,
    required: true,
    validator: (value) => ['homolog', 'production'].includes(value)
  },
  // Vem do GET de status: indica se o secret ja foi visualizado uma vez
  revealed: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['refresh'])

const { t } = useI18n()
const httpStore = useHttpStore()

const loading = ref(false)
const secret = ref(null)
const resultMode = ref('reveal')
const errorMessage = ref(null)
const showSecretModal = ref(false)
const showRegenConfirm = ref(false)

// 403 e 422 nao seguem o envelope { success, text, data, status } — tratados a parte
function messageForError(error) {
  const status = error?.response?.status
  const body = error?.response?.data

  switch (status) {
  case 409:
    return body?.text || t('credentialSecret.errors.alreadyRevealed')
  case 404:
    return body?.text || t('credentialSecret.errors.notFound')
  case 502:
    return t('credentialSecret.errors.regenerateFailed')
  case 422:
    return body?.stage?.[0] || t('credentialSecret.errors.invalidStage')
  case 403:
    return t('credentialSecret.errors.forbidden')
  default:
    return t('credentialSecret.errors.generic')
  }
}

async function reveal() {
  loading.value = true
  errorMessage.value = null
  try {
    const response = await httpStore.revealAppCredential(props.appId, props.stage)
    secret.value = response?.data?.data?.client_secret ?? null
    resultMode.value = 'reveal'
    showSecretModal.value = true
  } catch (error) {
    errorMessage.value = messageForError(error)
    // 409 = ja visualizado: atualiza o status para refletir revealed:true
    if (error?.response?.status === 409) {
      emit('refresh')
    }
  } finally {
    loading.value = false
  }
}

function openRegenConfirm() {
  errorMessage.value = null
  showRegenConfirm.value = true
}

function closeRegenConfirm() {
  showRegenConfirm.value = false
  errorMessage.value = null
}

async function confirmRegenerate() {
  loading.value = true
  errorMessage.value = null
  try {
    const response = await httpStore.regenerateAppCredential(props.appId, props.stage)
    secret.value = response?.data?.data?.client_secret ?? null
    resultMode.value = 'regenerate'
    showRegenConfirm.value = false
    showSecretModal.value = true
  } catch (error) {
    // Mantem o modal de confirmacao aberto para permitir nova tentativa (ex: 502)
    errorMessage.value = messageForError(error)
  } finally {
    loading.value = false
  }
}

function closeSecretModal() {
  showSecretModal.value = false
  secret.value = null
  errorMessage.value = null
  // Recarrega o status: o secret some do GET e revealed passa a true
  emit('refresh')
}
</script>

<style scoped>
.credential-secret__action {
  display: flex;
  flex-direction: column;
  gap: 8px;
  align-items: flex-start;
}

.credential-secret__hint {
  margin-top: 2px;
}

.credential-secret__note {
  display: flex;
  align-items: center;
  gap: 6px;
}

.credential-secret__note-icon {
  font-size: 18px;
  color: var(--text60, #6b7280);
}

.credential-secret__error {
  margin-top: 8px;
  padding: 8px 12px;
  border-radius: 6px;
  background: #fee2e2;
  color: #b91c1c;
  font-size: 13px;
}

.credential-secret__modal {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.credential-secret__warning {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 12px;
  border-radius: 8px;
  background: #fef9c3;
  color: #854d0e;
}

.credential-secret__warning-icon {
  font-size: 20px;
  flex-shrink: 0;
}

.credential-secret__modal-label {
  margin-top: 4px;
}

.credential-secret__modal-actions {
  display: flex;
  justify-content: flex-end;
  margin-top: 8px;
}

.credential-secret__modal-actions--split {
  justify-content: space-between;
  gap: 12px;
}
</style>
